/**
 * The Wild Turtle — cart-drawer.js
 * Ajax cart drawer with live updates
 */

(() => {
  'use strict';

  const routes = window.themeData?.routes || {};
  const moneyFormat = window.themeData?.money_format || '${{amount}}';

  /* =========================================================
     Money formatting
  ========================================================= */
  function formatMoney(cents) {
    return moneyFormat.replace(/\{\{\s*amount\s*\}\}/, (cents / 100).toFixed(2));
  }

  /* =========================================================
     API helpers
  ========================================================= */
  async function cartAdd(variantId, quantity = 1, properties = {}) {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ id: variantId, quantity, properties }),
    });
    if (!res.ok) throw new Error(`Cart add failed: ${res.status}`);
    return res.json();
  }

  async function cartChange(key, quantity) {
    const res = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ id: key, quantity }),
    });
    if (!res.ok) throw new Error(`Cart change failed: ${res.status}`);
    return res.json();
  }

  async function cartGet() {
    const res = await fetch('/cart.js', {
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
    });
    if (!res.ok) throw new Error(`Cart get failed: ${res.status}`);
    return res.json();
  }

  /* =========================================================
     Drawer open/close
  ========================================================= */
  const drawer  = document.getElementById('cart-drawer');
  const overlay = document.getElementById('page-overlay');
  let releaseTrap;

  function openDrawer() {
    if (!drawer) return;
    drawer.classList.add('is-open');
    drawer.removeAttribute('aria-hidden');
    drawer.removeAttribute('inert');
    overlay?.classList.add('is-visible');
    document.getElementById('cart-drawer-close')?.focus();
  }

  function closeDrawer() {
    if (!drawer) return;
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    drawer.setAttribute('inert', '');
    overlay?.classList.remove('is-visible');
    document.getElementById('cart-toggle')?.focus();
  }

  document.getElementById('cart-toggle')?.addEventListener('click', openDrawer);
  document.getElementById('cart-drawer-close')?.addEventListener('click', closeDrawer);
  overlay?.addEventListener('click', closeDrawer);
  document.addEventListener('keydown', e => { if (e.key === 'Escape') closeDrawer(); });

  /* =========================================================
     Loading state
  ========================================================= */
  function setLoading(loading) {
    const el = document.getElementById('cart-drawer-loading');
    if (el) loading ? el.removeAttribute('hidden') : el.setAttribute('hidden', '');
  }

  /* =========================================================
     Re-render cart items
  ========================================================= */
  async function refreshDrawer() {
    setLoading(true);
    try {
      const cart = await cartGet();
      renderCart(cart);
    } catch (err) {
      console.error('Cart refresh error:', err);
    } finally {
      setLoading(false);
    }
  }

  function renderCart(cart) {
    // Update count
    window.updateCartCount?.(cart.item_count);
    document.querySelectorAll('[data-cart-count]').forEach(el => {
      el.textContent = cart.item_count;
    });

    // Update subtotal
    document.querySelectorAll('[data-cart-subtotal]').forEach(el => {
      el.textContent = formatMoney(cart.total_price);
    });

    // Shipping bar
    const threshold = (window.themeData?.free_shipping_threshold || 5000);
    const progress  = Math.min(100, Math.floor((cart.total_price / threshold) * 100));
    document.querySelectorAll('.shipping-progress__fill').forEach(el => {
      el.style.width = progress + '%';
    });

    const shippingText = document.querySelector('.cart-drawer__shipping-bar > span');
    if (shippingText) {
      if (cart.total_price >= threshold) {
        shippingText.innerHTML = '🎉 You\'ve unlocked <strong>free shipping!</strong>';
      } else {
        const remaining = formatMoney(threshold - cart.total_price);
        shippingText.innerHTML = `Spend <strong>${remaining}</strong> more for free shipping`;
      }
    }

    // Items container
    const itemsEl = document.getElementById('cart-drawer-items');
    const footerEl = document.getElementById('cart-drawer-footer');
    if (!itemsEl) return;

    if (cart.item_count === 0) {
      itemsEl.innerHTML = `
        <div class="cart-drawer__empty" id="cart-drawer-empty">
          <div class="cart-drawer__empty-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/><path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/></svg>
          </div>
          <h3>Your cart is empty</h3>
          <p>Discover our eco-friendly swaps and start making a difference today.</p>
          <a href="/collections" class="btn btn--primary">Shop Now</a>
        </div>`;
      if (footerEl) footerEl.setAttribute('hidden', '');
      return;
    }

    if (footerEl) {
      footerEl.removeAttribute('hidden');
      const subtotalEl = footerEl.querySelector('[data-cart-subtotal]');
      if (subtotalEl) subtotalEl.textContent = formatMoney(cart.total_price);
    }

    itemsEl.innerHTML = cart.items.map(item => `
      <div class="cart-item" data-key="${item.key}" data-variant-id="${item.variant_id}">
        <a href="${item.url}" class="cart-item__image">
          ${item.image
            ? `<img src="${item.image.replace('http:', 'https:')}" alt="${escapeHtml(item.product_title)}" width="80" height="80" loading="lazy">`
            : '<div class="cart-item__image-placeholder"></div>'
          }
        </a>
        <div class="cart-item__info">
          <a href="${item.url}" class="cart-item__title">${escapeHtml(item.product_title)}</a>
          ${item.variant_title && item.variant_title !== 'Default Title'
            ? `<p class="cart-item__variant">${escapeHtml(item.variant_title)}</p>`
            : ''}
          ${item.selling_plan_allocation
            ? `<p class="cart-item__variant">${escapeHtml(item.selling_plan_allocation.selling_plan.name)}</p>`
            : ''}
          <div class="cart-item__bottom">
            <div class="qty-control" data-key="${item.key}">
              <button class="qty-control__btn" data-action="decrease" aria-label="Decrease quantity">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><line x1="5" y1="12" x2="19" y2="12"/></svg>
              </button>
              <input type="number" class="qty-control__value" value="${item.quantity}" min="0" aria-label="Quantity" data-key="${item.key}">
              <button class="qty-control__btn" data-action="increase" aria-label="Increase quantity">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
              </button>
            </div>
            <span class="cart-item__price">${formatMoney(item.final_line_price)}</span>
            <button class="cart-item__remove" data-key="${item.key}" aria-label="Remove ${escapeHtml(item.product_title)}">Remove</button>
          </div>
        </div>
      </div>`).join('');
  }

  function escapeHtml(str) {
    const map = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#039;' };
    return String(str).replace(/[&<>"']/g, m => map[m]);
  }

  /* =========================================================
     Quantity change in drawer
  ========================================================= */
  async function handleQtyChange(key, quantity) {
    setLoading(true);
    try {
      const cart = await cartChange(key, quantity);
      renderCart(cart);
    } catch (err) {
      console.error('Qty change error:', err);
    } finally {
      setLoading(false);
    }
  }

  // Delegated listener for qty buttons in drawer
  document.addEventListener('click', async e => {
    const btn = e.target.closest('.cart-drawer .qty-control__btn');
    if (!btn) return;
    const wrap     = btn.closest('.qty-control');
    const key      = wrap?.dataset.key;
    const input    = wrap?.querySelector('.qty-control__value');
    if (!key || !input) return;
    const current  = parseInt(input.value, 10);
    const action   = btn.dataset.action;
    const newQty   = action === 'increase' ? current + 1 : Math.max(0, current - 1);
    input.value    = newQty;
    await handleQtyChange(key, newQty);
  });

  // Input change
  document.addEventListener('change', async e => {
    const input = e.target.closest('.cart-drawer .qty-control__value');
    if (!input) return;
    const key    = input.dataset.key;
    const newQty = Math.max(0, parseInt(input.value, 10) || 0);
    if (key) await handleQtyChange(key, newQty);
  });

  // Remove button
  document.addEventListener('click', async e => {
    const btn = e.target.closest('.cart-item__remove');
    if (!btn) return;
    const key = btn.dataset.key;
    if (!key) return;
    setLoading(true);
    try {
      const cart = await cartChange(key, 0);
      renderCart(cart);
    } catch (err) {
      console.error('Remove error:', err);
    } finally {
      setLoading(false);
    }
  });

  /* =========================================================
     Quick Add to Cart (collection page cards)
  ========================================================= */
  document.addEventListener('click', async e => {
    const btn = e.target.closest('.product-card__atc, .coll-card__atc');
    if (!btn) return;
    const variantId = btn.dataset.variantId;
    if (!variantId) return;

    btn.classList.add('btn--loading');
    btn.disabled = true;

    try {
      await cartAdd(parseInt(variantId, 10), 1);
      await refreshDrawer();
      openDrawer();
      window.showCartNotification?.('Added to cart');
    } catch (err) {
      console.error('Quick add error:', err);
    } finally {
      btn.classList.remove('btn--loading');
      btn.disabled = false;
    }
  });

  /* =========================================================
     Expose globally
  ========================================================= */
  window.CartDrawer = { open: openDrawer, close: closeDrawer, refresh: refreshDrawer };

})();
